import React, { useState, useEffect } from 'react';
import { LayoutGrid, FileSpreadsheet, Settings } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { supabase } from './lib/supabase';
import { Session } from '@supabase/supabase-js';
import Dashboard from './components/Dashboard';
import Scanner from './components/Scanner';
import InventoryTable from './components/InventoryTable';
import SettingsModal from './components/SettingsModal';
import Auth from './components/Auth';
import { supabaseSync } from './lib/supabase';
import { checkSupabaseConnection } from './lib/checkEnv';

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [currentTab, setCurrentTab] = useState<'dashboard' | 'inventory'>('dashboard');
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [initAttempts, setInitAttempts] = useState(0);
  const maxInitAttempts = 3;

  useEffect(() => {
    let mounted = true;
    let initTimeout: NodeJS.Timeout | null = null;

    const initializeApp = async (attempt: number = 1) => {
      if (!mounted) return;

      try {
        // 연결 상태 체크
        const isConnected = await checkSupabaseConnection();
        if (!isConnected) {
          throw new Error('Supabase 연결에 실패했습니다');
        }

        // 초기화 타임아웃 설정
        initTimeout = setTimeout(() => {
          if (mounted && isLoading) {
            console.error(`Initialization timeout (attempt ${attempt}/${maxInitAttempts})`);
            if (attempt < maxInitAttempts) {
              setInitAttempts(attempt + 1);
              initializeApp(attempt + 1);
            } else {
              setInitError('초기화에 실패했습니다. 페이지를 새로고침해주세요.');
              setIsLoading(false);
            }
          }
        }, 20000);

        // 세션 가져오기
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) throw sessionError;

        if (mounted) {
          if (session) {
            console.log('Session found, initializing...');
            setSession(session);
            await supabaseSync.initialize(attempt, maxInitAttempts);
            console.log('Initialization complete');
          } else {
            console.log('No session found');
          }
          setIsLoading(false);
          setInitError(null);
          setInitAttempts(0);
        }
      } catch (error: any) {
        console.error(`Initialization error (attempt ${attempt}/${maxInitAttempts}):`, error);
        if (mounted) {
          if (attempt < maxInitAttempts) {
            console.log(`Retrying initialization in 2 seconds... (${attempt}/${maxInitAttempts})`);
            setInitAttempts(attempt + 1);
            setTimeout(() => initializeApp(attempt + 1), 2000);
          } else {
            setInitError(`초기화에 실패했습니다: ${error.message}`);
            setIsLoading(false);
          }
        }
      } finally {
        if (initTimeout) {
          clearTimeout(initTimeout);
          initTimeout = null;
        }
      }
    };

    // 인증 상태 변경 감지
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log('Auth state changed:', event);
      
      if (event === 'SIGNED_IN' && session) {
        setSession(session);
        await supabaseSync.initialize();
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        supabaseSync.destroy();
        window.location.reload();
      }
    });

    // 초기화 시작
    console.log('Starting initialization...');
    initializeApp();

    // 클린업
    return () => {
      console.log('Cleaning up...');
      mounted = false;
      if (initTimeout) {
        clearTimeout(initTimeout);
      }
      subscription?.unsubscribe();
      supabaseSync.destroy();
    };
  }, []);

  // 로딩 UI
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
        <p className="text-sm text-gray-600">초기화 중... ({initAttempts + 1}/{maxInitAttempts})</p>
      </div>
    );
  }

  // 오류 UI
  if (initError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md w-full mx-4">
          <h2 className="text-xl font-semibold text-red-600 mb-4">초기화 오류</h2>
          <p className="text-gray-700 mb-4">{initError}</p>
          <div className="space-y-2">
            <button
              onClick={() => window.location.reload()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              페이지 새로고침
            </button>
            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="w-full px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              캐시 초기화 후 새로고침
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 UI
  if (!session) {
    return (
      <>
        <Toaster position="top-right" />
        <Auth />
      </>
    );
  }

  // 메인 UI
  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" />
      
      <header className="bg-blue-600 text-white">
        <div className="container mx-auto px-4 py-3 md:px-6 md:py-0">
          <div className="flex flex-col md:flex-row md:h-16 md:items-center md:justify-between gap-3 md:gap-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileSpreadsheet className="w-8 h-8" />
                <h1 className="text-xl font-semibold">KWMS 무신사 입고관리</h1>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="md:hidden p-2 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
            
            <nav className="flex items-center space-x-2 md:space-x-6 pb-3 md:pb-0 overflow-x-auto">
              <button 
                onClick={() => setCurrentTab('dashboard')}
                className={`flex items-center space-x-2 px-3 md:px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  currentTab === 'dashboard' ? 'bg-blue-700' : 'hover:bg-blue-700'
                }`}
              >
                <LayoutGrid className="w-5 h-5" />
                <span>대시보드</span>
              </button>
              <button 
                onClick={() => setCurrentTab('inventory')}
                className={`flex items-center space-x-2 px-3 md:px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  currentTab === 'inventory' ? 'bg-blue-700' : 'hover:bg-blue-700'
                }`}
              >
                <FileSpreadsheet className="w-5 h-5" />
                <span>입고 처리</span>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="hidden md:flex items-center space-x-2 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                <Settings className="w-5 h-5" />
                <span>설정</span>
              </button>
              <button
                onClick={async () => {
                  try {
                    setIsLoading(true);
                    await supabase.auth.signOut();
                  } catch (error: any) {
                    console.error('로그아웃 실패:', error.message);
                    setInitError('로그아웃 중 오류가 발생했습니다.');
                  } finally {
                    setIsLoading(false);
                  }
                }}
                className="flex items-center space-x-2 px-3 md:px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                <span>로그아웃</span>
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-6 md:py-8">
        <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">
          {currentTab === 'dashboard' ? '대시보드' : '입고 처리'}
        </h2>
        <div className="space-y-4 md:space-y-6">
          {currentTab === 'dashboard' ? (
            <Dashboard />
          ) : (
            <>
              <Scanner />
              <InventoryTable />
            </>
          )}
        </div>
      </main>

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}

export default App;